import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth, useUser } from '@clerk/clerk-react';
import { RingLoader } from 'react-spinners';
import VideoPlayer from './VideoPlayer';
import styles from './VideoGenerationPage.module.css';
import TypingEffectTextWrapper from './TypingEffectTextWrapper';
import TempVideo from '../examples/1.mp4';
import TempVideoMobile from '../examples/1mobile.mp4';

interface VideoGenerationResult {
  status?: string;
  provider?: string;
  message?: string;
  videoUrl?: string | null;
  details?: any;
  prompt?: string;
  progress?: number;
  predictionId?: string;
}

interface VideoGenerationPageProps {
  onNavigateHome?: () => void;
}

const TEAM_OPTIONS = [
  'Algeria',
  'Argentina',
  'Australia',
  'Austria',
  'Belgium',
  'Bosnia and Herzegovina',
  'Brazil',
  'Canada',
  'Colombia',
  'Croatia',
  'Czech Republic',
  'Egypt',
  'England',
  'Equador',
  'Estonia',
  'France',
  'Germany',
  'Ghana',
  'Greece',
  'Hungary',
  'Israel',
  'Italy',
  'Ivory Coast',
  'Japan',
  'Mexico',
  'Morocco',
  'Netherlands',
  'New Zealand',
  'Nigeria',
  'Norway',
  'Poland',
  'Portugal',
  'Qatar',
  'Scotland',
  'Senegal',
  'Saudi Arabia',
  'South Africa',
  'South Korea',
  'Spain',
  'Sweden',
  'Switzerland',
  'Tunisia',
  'Turkey',
  'United States',
  'Uzbekistan'
];

// const ACTION_OPTIONS = ['wave', 'juggle', 'celebrate'];
const UPLOAD_HINTS = ['upload photo', 'or drop it here'];
// const EYEBROW_LINES = ['PhotoMotion - World Cup 2026 themed video generator', 'Developed by Andrei Karcheuski'];
const MOBILE_EYEBROW_LINES = ['World Cup 2026 AI-Powered App', 'Developed by Andrei Karcheuski'];
const EYEBROW_LINES = ['World Cup 2026 AI-Powered App', 'Developed by Andrei Karcheuski'];

type SpinnerOption = 'ring' | 'premium';

const PAYPAL_ME_URL = process.env.REACT_APP_PAYPAL_ME_URL || 'https://www.paypal.me/yourname';
const USDT_TRC20_ADDRESS = process.env.REACT_APP_USDT_TRC20_ADDRESS || 'TRC20 wallet address';
const USDC_POLYGON_ADDRESS = process.env.REACT_APP_USDC_POLYGON_ADDRESS || 'Polygon wallet address';
const PRODUCTION_API_URL = process.env.REACT_APP_API_URL || 'https://p01--astrostar-api--j9tk4rgqc56r.code.run';
const LOCAL_API_URL = 'http://localhost:8000';
const GENERATION_LIMIT_MESSAGE = 'Beta version allows only one video generation at the moment.';
const GENERATION_LIMIT_STORAGE_KEY = 'astrostar-video-generation-used';

const VideoGenerationPage: React.FC<VideoGenerationPageProps> = ({ onNavigateHome }) => {
  const [team, setTeam] = useState('');
  // const [action, setAction] = useState('');
  // for custom prompt, we can add a textarea input and manage its state
  const [prompt, setPrompt] = useState('');
  const [provider, setProvider] = useState('replicate');
  const [imageBase64, setImageBase64] = useState('');
  // const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string>();
  const [result, setResult] = useState<VideoGenerationResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedSpinner, setSelectedSpinner] = useState<SpinnerOption>('premium');
  const [loaderProgress, setLoaderProgress] = useState(0);
  const [loadingStatus, setLoadingStatus] = useState('Preparing your video');
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDonationOpen, setIsDonationOpen] = useState(false);
  const [copiedAddress, setCopiedAddress] = useState<string | null>(null);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const [uploadHintIndex, setUploadHintIndex] = useState(0);
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const [hasResolvedViewport, setHasResolvedViewport] = useState(false);
  const [eyebrowIndex, setEyebrowIndex] = useState(0);
  const [hasReachedGenerationLimit, setHasReachedGenerationLimit] = useState(false);
  // const [hasPendingCancel, setHasPendingCancel] = useState(false);
  const [isTeamDropdownOpen, setIsTeamDropdownOpen] = useState(false);
  const { isSignedIn, getToken } = useAuth();
  const { user } = useUser();
  const mobileResultRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const teamDropdownRef = useRef<HTMLDivElement | null>(null);
  // const activePredictionIdRef = useRef<string | null>(null);

  // useEffect(() => {
  //   const loadSampleImage = async () => {
  //     try {
  //       const response = await fetch('/sample-image.jpg');
  //       if (!response.ok) throw new Error('Sample image was not found.');
  //       const blob = await response.blob();
  //       const reader = new FileReader();
  //       reader.onload = () => {
  //         const dataUrl = reader.result as string;
  //         const base64 = dataUrl.split(',')[1] ?? '';
  //         setImageBase64(base64);
  //         setImagePreview(dataUrl);
  //       };
  //       reader.readAsDataURL(blob);
  //     } catch (err: any) {
  //       setError(err.message || 'Unable to preload the sample photo.');
  //     }
  //   };
  //   loadSampleImage();
  // }, []);

  const processSelectedFile = (file?: File | null) => {
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const base64 = dataUrl.split(',')[1] ?? '';
      setImageBase64(base64);
      setImagePreview(dataUrl);
    };
    reader.readAsDataURL(file);
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    processSelectedFile(file);
    event.target.value = '';
  };

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDraggingFile(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (event.currentTarget.contains(event.relatedTarget as Node | null)) {
      return;
    }
    setIsDraggingFile(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDraggingFile(false);
    const file = event.dataTransfer.files?.[0];
    processSelectedFile(file);
  };

  const getApiEndpoint = (path: string) => {
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const baseUrl = isLocalhost ? LOCAL_API_URL : PRODUCTION_API_URL;
    // const baseUrl = PRODUCTION_API_URL;
    return `${baseUrl}${path.startsWith('/') ? '' : '/'}${path}`;
  };

  const persistGenerationLimit = (value: boolean) => {
    setHasReachedGenerationLimit(value);
    try {
      window.localStorage.setItem(GENERATION_LIMIT_STORAGE_KEY, value ? 'true' : 'false');
    } catch {
      // Ignore storage errors and continue with in-memory state only.
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (hasReachedGenerationLimit) {
      setError(GENERATION_LIMIT_MESSAGE);
      return;
    }

    // activePredictionIdRef.current = null;
    // setHasPendingCancel(false);
    setIsLoading(true);
    setError('');
    setResult(null);
    setLoaderProgress(0);
    setLoadingStatus('Creating your video');

    try {
      if (!isSignedIn) {
        throw new Error('Please sign in before generating a video.');
      }

      const token = await getToken();
      const payload: any = {
        imageBase64: imageBase64 || '',
        team,
        // action,
        provider,
      };
      if (prompt.trim()) {
        payload.prompt = prompt.trim();
      }

      const response = await fetch(getApiEndpoint('/generate-video'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(data?.detail || 'The video generation request failed.');
      }

      const normalized = data || {};
      // activePredictionIdRef.current = normalized?.predictionId || null;
      setResult(normalized);
      setLoaderProgress(Math.round((Number(normalized.progress) || 0) * 100));
      setLoadingStatus(normalized.message || 'Video generation started.');

      const normalizedStatus = String(normalized.status || '').toLowerCase();
      const hasSuccessfulVideo = Boolean(normalized.videoUrl) || ['succeeded', 'completed', 'ready'].includes(normalizedStatus);
      if (hasSuccessfulVideo) {
        persistGenerationLimit(true);
      }

      if (normalized.status && ['succeeded', 'failed', 'canceled'].includes(normalizedStatus)) {
        setIsLoading(false);
      }
    } catch (err: any) {
      setError(err.message || 'Something went wrong while generating the video.');
      setIsLoading(false);
    }
  };

  const videoSource = useMemo(() => {
    if (result?.videoUrl) return result.videoUrl;
    // if (result?.status === 'mock') return FallbackVideo;
    // return FallbackVideo;
  }, [result]);

  const canDownload = useMemo(() => {
    const normalizedStatus = String(result?.status || '').toLowerCase();
    return Boolean(result?.videoUrl && ['succeeded', 'completed', 'ready'].includes(normalizedStatus));
  }, [result?.status, result?.videoUrl]);

  // const handleCancelGeneration = async () => {
  //   const predictionId = activePredictionIdRef.current || result?.predictionId;
  //   if (!predictionId) {
  //     setHasPendingCancel(true);
  //     setError('');
  //     setLoadingStatus('Cancelling video generation...');
  //     return;
  //   }

  //   setHasPendingCancel(false);
  //   setError('');
  //   setLoadingStatus('Cancelling video generation...');
  //   setResult((prev) => prev ? { ...prev, status: 'canceled', message: 'Cancelling video generation...', progress: 0 } : prev);
  //   setIsLoading(false);

  //   try {
  //     const response = await fetch(getApiEndpoint(`/cancel-video/${encodeURIComponent(predictionId)}`), {
  //       method: 'POST',
  //       headers: { 'Content-Type': 'application/json' },
  //     });
  //     const data = await response.json().catch(() => null);

  //     if (!response.ok) {
  //       throw new Error(data?.detail || 'The cancel request failed.');
  //     }

  //     setResult((prev) => prev ? { ...prev, ...data, status: 'canceled', message: data?.message || 'Video generation canceled.', progress: 0 } : prev);
  //     setLoadingStatus(data?.message || 'Video generation canceled.');
  //   } catch (err: any) {
  //     setError(err.message || 'Something went wrong while canceling the video generation.');
  //     setLoadingStatus('Ready for a new video');
  //   }
  // };

  const handleDownloadVideo = async () => {
    const downloadKey = result?.predictionId || result?.details?.prediction_id;
    if (!downloadKey) {
      setError('The video is not ready for download yet.');
      return;
    }

    setIsDownloading(true);
    setError('');

    try {
      const response = await fetch(getApiEndpoint(`/download-generated-video/${encodeURIComponent(downloadKey)}`), {
        headers: { Accept: 'application/octet-stream' },
      });

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.detail || 'The video could not be downloaded.');
      }

      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const match = disposition.match(/filename="?([^";]+)"?/i);
      const filename = match?.[1] || 'generated-video.mp4';
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
      setLoadingStatus('Video downloaded successfully.');
    } catch (err: any) {
      setError(err.message || 'Something went wrong while downloading the video.');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleCopyAddress = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedAddress(label);
      window.setTimeout(() => setCopiedAddress((prev) => (prev === label ? null : prev)), 1800);
    } catch {
      setError(`Unable to copy ${label}. Please copy it manually.`);
    }
  };

  const openDonationLink = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const renderSelectedSpinner = () => {
    switch (selectedSpinner) {
      case 'ring':
        return <RingLoader color="#3498db" size={90} />;
      case 'premium':
        return (
          <div className={styles.premiumShell}>
            <RingLoader color="#3498db" size={68} />
            <div className={styles.progressBar} aria-hidden>
              <div className={styles.progressFill} style={{ width: `${loaderProgress}%` }} />
            </div>
          </div>
        );
      default:
        return <RingLoader color="#3498db" size={90} />;
    }
  };

  useEffect(() => {
    try {
      const storedValue = window.localStorage.getItem(GENERATION_LIMIT_STORAGE_KEY);
      if (storedValue === 'true') {
        setHasReachedGenerationLimit(true);
      }
    } catch {
      // Ignore storage access issues and fall back to the default state.
    }
  }, []);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setUploadHintIndex((prev) => (prev + 1) % UPLOAD_HINTS.length);
    }, 2200);

    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const handleViewportChange = (event: MediaQueryListEvent | MediaQueryList) => {
      const matches = 'matches' in event ? event.matches : mediaQuery.matches;
      setIsMobileViewport(matches);
      setHasResolvedViewport(true);
    };

    handleViewportChange(mediaQuery);

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleViewportChange);
      return () => mediaQuery.removeEventListener('change', handleViewportChange);
    }

    mediaQuery.addListener(handleViewportChange);
    return () => mediaQuery.removeListener(handleViewportChange);
  }, []);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setEyebrowIndex((prev) => (prev + 1) % EYEBROW_LINES.length);
    }, 1800);

    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (teamDropdownRef.current && !teamDropdownRef.current.contains(event.target as Node)) {
        setIsTeamDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, []);

  useEffect(() => {
    if (!isLoading) {
      setLoaderProgress(100);
      const t = window.setTimeout(() => setLoaderProgress(0), 700);
      return () => window.clearTimeout(t);
    }

    const progressValue = Number(result?.progress ?? NaN);
    if (!Number.isNaN(progressValue) && progressValue > 0) {
      setLoaderProgress(Math.round(progressValue * 100));
    } else {
      setLoaderProgress((prev) => Math.max(prev, 8));
    }
  }, [isLoading, result?.progress]);

  useEffect(() => {
    if (!isMobileViewport || !canDownload) {
      return;
    }

    mobileResultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [canDownload, isMobileViewport]);

  // useEffect(() => {
  //   if (!hasPendingCancel || !result?.predictionId) {
  //     return;
  //   }

  //   activePredictionIdRef.current = result.predictionId;
  //   setHasPendingCancel(false);
  //   void handleCancelGeneration();
  // }, [hasPendingCancel, result?.predictionId]);

  useEffect(() => {
    if (!isLoading || !result?.predictionId) {
      return;
    }

    const predictionId = result.predictionId;
    if (!predictionId) {
      return;
    }

    let cancelled = false;

    const pollProgress = async () => {
      try {
        const response = await fetch(getApiEndpoint(`/video-progress/${encodeURIComponent(predictionId)}`), {
          headers: { Accept: 'application/json' },
        });
        const data = await response.json().catch(() => null);

        if (!cancelled && data) {
          setResult((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              ...data,
              predictionId: prev.predictionId ?? data.predictionId,
              progress: data.progress ?? prev.progress,
              videoUrl: data.videoUrl ?? prev.videoUrl,
              message: data.message ?? prev.message,
              details: { ...prev.details, ...(data.details || {}) },
            };
          });

          const normalizedStatus = String(data.status || '').toLowerCase();
          setLoadingStatus(data.message || 'Processing your video...');

          const hasSuccessfulVideo = Boolean(data.videoUrl) || ['succeeded', 'completed', 'ready'].includes(normalizedStatus);
          if (hasSuccessfulVideo) {
            persistGenerationLimit(true);
          }

          if (['succeeded', 'failed', 'canceled'].includes(normalizedStatus)) {
            setIsLoading(false);
            setLoaderProgress(normalizedStatus === 'succeeded' ? 100 : 0);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setLoadingStatus('Waiting for video progress updates...');
        }
      }
    };

    void pollProgress();
    const intervalId = window.setInterval(() => {
      void pollProgress();
    }, 1000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [isLoading, result?.predictionId]);

  const renderUploadDropzone = (compact = false) => (
    <div className={styles.uploadField}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className={styles.hiddenInput}
      />
      <div
        className={`${styles.uploadDropzone} ${isDraggingFile ? styles.uploadDropzoneActive : ''} ${compact ? styles.uploadDropzoneCompact : ''}`}
        onClick={openFilePicker}
        onDragEnter={handleDragOver}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openFilePicker();
          }
        }}
      >
        <div className={styles.uploadContentRow}>
          <div className={styles.uploadTextBlock}>
            <span className={styles.uploadPrimaryText}>
              {imagePreview ? '⬆️Photo uploaded' : `⬆️${UPLOAD_HINTS[uploadHintIndex]}`}
            </span>
            <span className={styles.uploadHintBadge}>
              {imagePreview ? 'Tap to replace' : 'Tap to browse'}
            </span>
          </div>
          {imagePreview && (
            <img src={imagePreview} alt="Uploaded preview" className={styles.uploadPreviewImage} />
          )}
        </div>
      </div>
    </div>
  );

  // const showMobileVideoPanel = isMobileViewport && (isLoading || Boolean(videoSource || result));
  const showMobileVideoPanel = isMobileViewport;
  // const isEmptyPreviewState = !isLoading && !videoSource;
  const isEmptyPreviewState = !videoSource;
  const previewCardClassName = `${styles.previewCard} ${isEmptyPreviewState ? styles.previewCardEmptyState : ''}`.trim();
  const videoFrameClassName = `${styles.videoFrame} ${isEmptyPreviewState ? styles.videoFrameEmptyState : ''}`.trim();

  return (
    <div className={styles.page}>
      {/* <div className={styles.hero}>
        <div className={styles.heroColumn}> */}
      <div className={styles.heroContent}>
        <div className={styles.eyebrowRoller}>
          <span key={eyebrowIndex} className={styles.eyebrow}>
            {isMobileViewport ? MOBILE_EYEBROW_LINES[eyebrowIndex] : EYEBROW_LINES[eyebrowIndex]}
          </span>
        </div>
        {/* <div className={styles.header}>
              <TypingEffectTextWrapper
                text="Turn a selfie into a football-team video"
                speed={30}
                alwaysStart={true}
                reserveFullWidth={false}
                horizontalAlign="left"
                style={{ color: '#032d60' }}
              />
            </div>
          </div>
        </div>
        <div className={styles.heroColumn}>
          <p className={styles.subtitle}>
            Upload a photo, pick your favorite team, and generate video of you in the football team jersey.
          </p>
        </div> */}
      </div>

      <div className={styles.grid}>
        <form className={styles.formCard} onSubmit={handleSubmit}>
          {showMobileVideoPanel && (
            <div className={styles.mobileStagePanel} ref={mobileResultRef}>
              <div className={styles.mobileVideoFrame}>
                {isLoading ? (
                  <div className={styles.loadingState}>
                    <div className={styles.spinnerShell}>{renderSelectedSpinner()}</div>
                    <p className={styles.loadingText}>{loadingStatus}</p>
                    <p className={styles.loadingText}>It might take a few minutes...</p>
                  </div>
                ) :
                  //  videoSource ? (
                  <VideoPlayer
                    src={videoSource ? videoSource : TempVideoMobile}
                    autoplay={true}
                    controls={false}
                    muted
                    loop={!videoSource}
                    showPreview={false}
                    style={{ width: '100%', maxWidth: '100%', aspectRatio: 'auto', minHeight: 250 }}
                  />
                  // ) : (
                  //   // <div className={styles.emptyState}>No video generated yet.</div>
                  //   <div className={styles.emptyState}>
                  //     <TypingEffectTextWrapper
                  //       text="Upload a photo, pick your favorite team, and generate video of you in the football team jersey"
                  //       speed={15}
                  //       alwaysStart={true}
                  //       reserveFullWidth={false}
                  //       horizontalAlign="center"
                  //     />
                  //   </div>
                  // )
                }
              </div>
            </div>
          )}
          <div className={styles.field}>
            <div className={styles.customSelectWrapper} ref={teamDropdownRef}>
              <button
                type="button"
                className={`${styles.dropdownControl} ${isTeamDropdownOpen ? styles.dropdownControlOpen : ''}`}
                onClick={() => setIsTeamDropdownOpen((prev) => !prev)}
                aria-haspopup="listbox"
                aria-expanded={isTeamDropdownOpen}
              >
                <span className={styles.dropdownControlLabel}>{team || 'Select your favorite team'}</span>
                <span className={styles.dropdownArrow}>▾</span>
              </button>

              {isTeamDropdownOpen && (
                <div className={styles.dropdownMenu} role="listbox">
                  {TEAM_OPTIONS.map((option, index) => (
                    <button
                      key={option}
                      type="button"
                      className={styles.dropdownOption}
                      style={{ animationDelay: `${index * 45}ms` }}
                      onClick={() => {
                        setTeam(option);
                        setIsTeamDropdownOpen(false);
                      }}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* {isMobileViewport && isLoading && (
            <div className={styles.mobileProgressHeader}>
              <div className={styles.mobileProgressTitle}>Generating your team video</div>
              <div className={styles.mobileProgressSummary}>
                <span className={styles.mobileProgressBadge}>{loaderProgress}%</span>
                <p>{loadingStatus}</p>
              </div>
            </div>
          )} */}

          {/* <label className={styles.field}>
            <select value={action} onChange={(e) => setAction(e.target.value)} className={styles.select}>
              <option value="" disabled hidden>Choose an action</option>
              {ACTION_OPTIONS.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </label> */}
          {/* 
          <label className={styles.field}>
            <select value={provider} onChange={(e) => setProvider(e.target.value)} className={styles.select}>
              <option value="" disabled hidden>Choose a video provider</option>
              <option value="luma">Luma</option>
              <option value="fal">Fal</option>
              <option value="replicate">Replicate</option>
            </select>
          </label> */}

          {(!isMobileViewport || !isLoading) && renderUploadDropzone(isMobileViewport && Boolean(result) && !isLoading)}

          {!isMobileViewport && imagePreview && (
            <div className={styles.previewImageFrame}>
              <img src={imagePreview} alt="Selected portrait" className={styles.previewImage} />
            </div>
          )}
          <div className={styles.actionStack}>
            {/* {isLoading ? (
              <button
                type="button"
                className={styles.cancelButton}
                onClick={handleCancelGeneration}
                disabled={hasPendingCancel}
              >
                {hasPendingCancel ? 'Cancelling…' : 'Cancel'}
              </button>
            ) : ( */}
            <button
              type="submit"
              className={styles.primaryButton}
              disabled={isLoading || !imageBase64 || !team || hasReachedGenerationLimit}
              onClick={() => {
                if (hasReachedGenerationLimit) {
                  setError(GENERATION_LIMIT_MESSAGE);
                  return;
                }
                setResult(null);
                setLoadingStatus('Ready for a new video');
              }}
            >
              {hasReachedGenerationLimit ? 'Limit reached' : isLoading ? 'Generating…' : canDownload ? 'Generate another' : 'Generate'}
            </button>
            {canDownload && isMobileViewport && (
              <><button
                type="button"
                className={styles.downloadButton}
                onClick={handleDownloadVideo}
                disabled={isDownloading}
              >
                {isDownloading ? 'Downloading…' : '⬇ Download'}
              </button><button type="button" className={styles.donateButton} onClick={() => setIsDonationOpen(true)}>
                  ☕ Support with coffee
                </button><button
                  type="button"
                  className={styles.secondaryButton}
                  onClick={() => onNavigateHome?.()}
                >
                  Explore my services
                </button></>
            )}
          </div>
          {error && <p className={styles.error}>{error}</p>}
        </form>

        {!isMobileViewport && (<div className={previewCardClassName}>
          <div className={videoFrameClassName}>
            {isLoading ? (
              <div className={styles.loadingState}>
                <div className={styles.spinnerShell}>{renderSelectedSpinner()}</div>
                <p className={styles.loadingText}>{loadingStatus}</p>
                <p className={styles.loadingText}>It might take a few minutes...</p>
              </div>
            ) :
              // videoSource ? (  
              <VideoPlayer
                src={videoSource ? videoSource : TempVideo}
                // src={TempVideo}
                autoplay={true}
                controls={false}               
                muted
                loop={!videoSource}         
                showPreview={false}
                style={{ width: 'fit-content', maxWidth: '100%', height: 715, aspectRatio: 'auto', minHeight: 0, margin: 0 }}
              />
              // )
              //  : (
              //   <div className={styles.emptyState}>No video generated yet.</div>
              // )
            }
          </div>

          {canDownload && (
            <div className={styles.previewActions}>
              <button
                type="button"
                className={styles.downloadButton}
                onClick={handleDownloadVideo}
                disabled={isDownloading}
              >
                {isDownloading ? 'Downloading…' : '⬇ Download'}
              </button>
              <button type="button" className={styles.donateButton} onClick={() => setIsDonationOpen(true)}>
                ☕ Support with coffee
              </button>
              <button
                type="button"
                className={styles.secondaryButton}
                onClick={() => onNavigateHome?.()}
              >
                Explore my services
              </button>
            </div>
          )}

          {/* {!isMobileViewport && result && (
            <div className={styles.resultBox}>
              <h3>Request status</h3>
              <p><strong>Status:</strong> {result.status || 'unknown'}</p>
              <p><strong>Message:</strong> {result.message || 'Video request completed.'}</p>
              {result.details?.status_code && <p><strong>HTTP status:</strong> {result.details.status_code}</p>}
            </div>
          )} */}
        </div>
        )}
      </div>

      {isDonationOpen && (
        <div className={styles.modalOverlay} onClick={() => setIsDonationOpen(false)}>
          <div className={styles.modalCard} onClick={(event) => event.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div>
                <p className={styles.modalEyebrow}>Support the project</p>
                <h3>Buy me a coffee</h3>
              </div>
              <button type="button" className={styles.modalClose} onClick={() => setIsDonationOpen(false)}>
                ×
              </button>
            </div>

            <p className={styles.modalText}>
              A small donation helps keep this project going. Choose the option that works best for you.
            </p>

            <div className={styles.donationOption}>
              <div>
                <h4>PayPal</h4>
                <p>Fastest option for a small coffee donation.</p>
              </div>
              <button type="button" className={styles.modalPrimaryButton} onClick={() => openDonationLink(PAYPAL_ME_URL)}>
                Open PayPal.me
              </button>
            </div>

            <div className={styles.donationOption}>
              <div>
                <h4>USDT (TRC-20)</h4>
                <p className={styles.addressText}>{USDT_TRC20_ADDRESS}</p>
              </div>
              <button type="button" className={styles.modalSecondaryButton} onClick={() => handleCopyAddress(USDT_TRC20_ADDRESS, 'USDT TRC-20 address')}>
                {copiedAddress === 'USDT TRC-20 address' ? 'Copied!' : 'Copy address'}
              </button>
            </div>

            <div className={styles.donationOption}>
              <div>
                <h4>USDC (Polygon)</h4>
                <p className={styles.addressText}>{USDC_POLYGON_ADDRESS}</p>
              </div>
              <button type="button" className={styles.modalSecondaryButton} onClick={() => handleCopyAddress(USDC_POLYGON_ADDRESS, 'USDC Polygon address')}>
                {copiedAddress === 'USDC Polygon address' ? 'Copied!' : 'Copy address'}
              </button>
            </div>

            {/* <p className={styles.modalHint}>
              Tip: if you want to use your real links and wallet addresses, set the environment variables for PayPal, USDT, and USDC before building.
            </p> */}
          </div>
        </div>
      )}

      {/* <div className={styles.spinnerSection}>
        <div className={styles.spinnerSectionHeader}>
          <h2>Compare loading animations</h2>
          <p>Choose the style that feels best for your generation experience.</p>
        </div>
        <SpinnerComparison
          message={isLoading ? 'Generating your video...' : 'Pick a loader style to use during generation.'}
          selected={selectedSpinner}
          onSelect={() => setSelectedSpinner('premium')}
        />
      </div> */}
    </div>
  );
};

export default VideoGenerationPage;